import React from "react";
import { useParams } from "react-router-dom";

// Components
import SEO from "components/CBSEO";
import SchemaComponent from "components/CBSchema";
import PageHeader from "components/CBPageHeader";
import { StyledMainPageContainer } from "index.styles";
import { StyledSectionBackgroundWrapper } from "index.styles";

// Reusable component
import GhlFormEmbed from "components/GhlFormEmbed";

// Utils
import { constructSchemaData } from "utils/helpers/constructSchemaData";

// SEO Data
import checkCoverageSeoData from "./data/seoData/checkCoverageSeo.data.json";

// Helper function to format modality type for display
const formatModalityType = (modalityType) => {
  return modalityType
    ?.split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const InsuranceCheckCoverage = () => {
  const { modalityType } = useParams();
  const baseSeoData = checkCoverageSeoData;

  // Create dynamic breadcrumbs based on modalityType presence
  const formattedModality = formatModalityType(modalityType);

  const dynamicBreadcrumbs = modalityType
    ? [
        { name: "Home", url: "/" },
        { name: formattedModality, url: `/${modalityType}` },
        { name: `${formattedModality} Insurance Options`, url: `/${modalityType}/insurance-options` },
        { name: "Check Insurance Coverage", url: `/${modalityType}/insurance-options/check-coverage` },
      ]
    : [
        { name: "Home", url: "/" },
        { name: "Insurance Options", url: "/insurance-options" },
        { name: "Check Insurance Coverage", url: "/insurance-options/check-coverage" },
      ];

  // Create dynamic canonical URL - handles both patterns
  const dynamicCanonicalUrl = `https://www.almadelic.com/${
    modalityType ? `${modalityType}/insurance-options/check-coverage` : `insurance-options/check-coverage`
  }`;

  // Create dynamic SEO data
  const dynamicSeoData = {
    ...baseSeoData,
    title: modalityType
      ? baseSeoData.title?.replace("| Almadelic", `| ${formattedModality} | Almadelic`) ||
        `Check Your Insurance Coverage for ${formattedModality} | Almadelic`
      : baseSeoData.title || "Check Your Insurance Coverage | Almadelic",
    description: modalityType
      ? baseSeoData.description ||
        `Verify your insurance coverage for ${modalityType} and mental health treatment. Check if your insurance plan covers our ${formattedModality} services with Almadelic.`
      : baseSeoData.description ||
        "Verify your insurance coverage for mental health treatment and therapy services. Check if your insurance plan covers our services with Almadelic.",
    canonicalUrl: dynamicCanonicalUrl,
  };

  // Create dynamic schema data
  const dynamicSchemaData = constructSchemaData(dynamicCanonicalUrl, dynamicBreadcrumbs);

  // Dynamic header content
  const dynamicHeader = modalityType ? `Check Your Insurance Coverage \\n for ${formattedModality}` : "Check Your Insurance Coverage";

  const dynamicHeaderAccent = modalityType ? [`Check Your Insurance Coverage`] : ["Check Your Insurance Coverage"];

  const dynamicHeaderTagline = modalityType
    ? `Enter your information below to check your insurance coverage for our ${formattedModality} services.`
    : "Enter your information below to check your insurance coverage for our services.";

  return (
    <StyledMainPageContainer id={`${modalityType ? `${modalityType}-` : ""}insurance-check-coverage-page`}>
      <SEO title={dynamicSeoData.title} description={dynamicSeoData.description} canonicalUrl={dynamicSeoData.canonicalUrl} />
      <SchemaComponent schemas={dynamicSchemaData} />

      <StyledSectionBackgroundWrapper>
        <PageHeader
          headerType="default"
          header={dynamicHeader}
          headerAccent={dynamicHeaderAccent}
          headerTagline={dynamicHeaderTagline}
          lightText="true"
        />
      </StyledSectionBackgroundWrapper>
      <GhlFormEmbed formId="p28M8AHI0vGL4T5bobux" formType="form" title="Check Insurance Coverage" height="100vh" />
    </StyledMainPageContainer>
  );
};

export default InsuranceCheckCoverage;
